/**
 * Mapeia Bundle de Appointment → itens da fila PA + shape para handleStartService.
 */
import dayjs from "dayjs";
import { useFhirData } from "@/utils/useFhirData";
import { useFormatter } from "@/utils/useFormatter";
import { PRONTO_ATENDIMENTO_TYPE_SERVICE } from "./useProntoAtendimentoAppointment";
import type { ProntoAtendimentoQueueItem } from "./types";

/** Shape esperado por useAppointmentActions / ScheduleQueueActions. */
export type ProntoActionAppointment = {
  id?: string;
  status?: string;
  startTime?: string;
  endTime?: string;
  patientName?: string;
  patientPhone?: string;
  practitioner?: string;
  healthcare?: string;
  originalData: fhir5.Appointment;
  [key: string]: unknown;
};

export interface ProntoQueueRow extends ProntoAtendimentoQueueItem {
  /** Appointment completo para iniciar atendimento. */
  actionAppointment: ProntoActionAppointment;
}

/** Confirma appointmentType de Pronto Atendimento (filtro client-side). */
export function isProntoAtendimentoAppointment(
  appointment: fhir5.Appointment | undefined,
): boolean {
  if (!appointment) return false;

  const codingCode = appointment.appointmentType?.coding?.[0]?.code || "";
  const text = appointment.appointmentType?.text || "";

  return (
    codingCode === PRONTO_ATENDIMENTO_TYPE_SERVICE ||
    text === PRONTO_ATENDIMENTO_TYPE_SERVICE ||
    text === "Pronto Atendimento"
  );
}

/**
 * Extrai appointments + patients de um Bundle (com _include).
 * Filtra prontoatendimento e ordena por start.
 */
export function mapBundleToProntoQueueRows(
  bundle: fhir5.Bundle | undefined,
  inProgressAppointmentId?: string,
): ProntoQueueRow[] {
  if (!bundle?.entry?.length) return [];

  const patientsById = new Map<string, fhir5.BundleEntry<fhir5.Patient>>();
  const appointmentEntries: fhir5.BundleEntry<fhir5.Appointment>[] = [];

  bundle.entry.forEach((entry) => {
    const res = entry.resource;
    if (!res) return;
    if (res.resourceType === "Patient" && res.id) {
      patientsById.set(res.id, entry as fhir5.BundleEntry<fhir5.Patient>);
    }
    if (res.resourceType === "Appointment") {
      appointmentEntries.push(entry as fhir5.BundleEntry<fhir5.Appointment>);
    }
  });

  const rows: ProntoQueueRow[] = [];

  appointmentEntries.forEach((entry) => {
    const appointment = entry.resource;
    if (!appointment?.id) return;
    if (!isProntoAtendimentoAppointment(appointment)) return;

    const patientRef = appointment.participant?.find((p) =>
      p.actor?.reference?.includes("Patient/"),
    )?.actor?.reference;
    const patientId = patientRef?.split("/")?.[1];
    const patientEntry = patientId ? patientsById.get(patientId) : undefined;

    const extracted = useFhirData.extractAppointmentData(
      entry,
      patientEntry,
      undefined,
    );

    // Nome/telefone vêm do Patient incluído (tipagem de extractAppointmentData não expõe patientName)
    const displayName =
      patientEntry?.resource?.name?.[0]?.text ||
      appointment.participant?.find((p) =>
        p.actor?.reference?.includes("Patient/"),
      )?.actor?.display ||
      "Paciente";

    const patientPhone =
      patientEntry?.resource?.telecom?.find((t) => t.system === "phone")
        ?.value || undefined;

    const actionAppointment: ProntoActionAppointment = {
      ...extracted,
      originalData: appointment,
      patientName: displayName,
      patientPhone,
    };

    const uiStatus: ProntoAtendimentoQueueItem["status"] =
      inProgressAppointmentId &&
      String(inProgressAppointmentId) === String(appointment.id)
        ? "in_progress"
        : "waiting";

    rows.push({
      id: appointment.id,
      patientName: useFormatter.formatNameDisplay(String(displayName)),
      timeLabel: appointment.start
        ? dayjs(appointment.start).format("HH:mm")
        : "--:--",
      status: uiStatus,
      actionAppointment,
    });
  });

  rows.sort((a, b) => {
    const sa = a.actionAppointment.originalData.start || "";
    const sb = b.actionAppointment.originalData.start || "";
    return sa.localeCompare(sb);
  });

  return rows;
}
